import { FormGroup } from '@angular/forms';
import { isEqual } from 'lodash-es';
import { isAfter } from 'date-fns';
import { addRemoveError, getControl, getFormArray, getGroup } from '@shared/helpers/form.helper';
import { isNilOrEmpty } from '@shared/helpers/utils.helper';
import { DATE_NOT_ALLOWED, REQUIRED_STATUS } from '@shared/constants/shared.constants';
import { CkNormeValues } from '../common/models/amm-controllo.type';
import { codiceFiscaleHelper, futureOrInconsistentDate, ruleGuard } from './amm-controllo-validators.helpers';
import { formPaths } from '@shared/constants/form-paths.const';

const LIQUIDATORI_ARRAY_PATH = `${formPaths.ammControllo}.my_AllLiquidatori.my_AllElemLiquidatori`;

export const liquidatoriRules = (ckNormeTargetValue: CkNormeValues) => ({
    my_AllLiquidatoriDataElezione: (form: FormGroup) => {
        ruleGuard(form, ckNormeTargetValue, [], () => {
            const my_AllLiquidatoriFormArray = getFormArray(form, LIQUIDATORI_ARRAY_PATH);

            my_AllLiquidatoriFormArray.controls.forEach((group: FormGroup) => {
                const my_AllLiquidatoriDataElezioneControl = getControl(group, 'my_AllLiquidatoriDataElezione');
                futureOrInconsistentDate(form, my_AllLiquidatoriDataElezioneControl, 'my_AllLiquidatoriDataElezione', ckNormeTargetValue);
            });
        });
    },

    my_CkLiquidatoriRelazionano: (form: FormGroup) => {
        ruleGuard(form, ckNormeTargetValue, [], () => {
            const amministrazioneControlloGroup = getGroup(form, formPaths.ammControllo);
            const my_CkLiquidatoriRelazionanoControl = getControl(amministrazioneControlloGroup, 'my_CkLiquidatoriRelazionano');
            const my_AllElemLiquidatori: string = getControl(amministrazioneControlloGroup, 'my_AllLiquidatori.my_AllElemLiquidatori').value;
            const errors = my_CkLiquidatoriRelazionanoControl.errors ? my_CkLiquidatoriRelazionanoControl.errors : {};

            addRemoveError(isNilOrEmpty(my_CkLiquidatoriRelazionanoControl?.value) && !isNilOrEmpty(my_AllElemLiquidatori), errors, 'my_CkLiquidatoriRelazionano', {
                status: REQUIRED_STATUS,
            });

            my_CkLiquidatoriRelazionanoControl.setErrors(isEqual(errors, {}) ? null : errors);
        });
    },

    my_CriteriRelazioneLiquidatori: (form: FormGroup) => {
        ruleGuard(form, ckNormeTargetValue, [], () => {
            const amministrazioneControlloGroup = getGroup(form, formPaths.ammControllo);
            const my_CriteriRelazioneLiquidatoriControl = getControl(amministrazioneControlloGroup, 'my_CriteriRelazioneLiquidatori');
            const my_AllElemLiquidatori: string = getControl(amministrazioneControlloGroup, 'my_AllLiquidatori.my_AllElemLiquidatori').value;
            const my_CkLiquidatoriRelazionano: string = getControl(amministrazioneControlloGroup, 'my_CkLiquidatoriRelazionano').value;
            const errors = my_CriteriRelazioneLiquidatoriControl.errors ? my_CriteriRelazioneLiquidatoriControl.errors : {};

            addRemoveError(
                isNilOrEmpty(my_CriteriRelazioneLiquidatoriControl?.value) && my_CkLiquidatoriRelazionano === '1' && !isNilOrEmpty(my_AllElemLiquidatori),
                errors,
                'my_CriteriRelazioneLiquidatori',
                {
                    status: REQUIRED_STATUS,
                }
            );

            my_CriteriRelazioneLiquidatoriControl.setErrors(isEqual(errors, {}) ? null : errors);
        });
    },

    my_CkRegistroDelibLiquidatori: (form: FormGroup) => {
        ruleGuard(form, ckNormeTargetValue, [], () => {
            const amministrazioneControlloGroup = getGroup(form, formPaths.ammControllo);
            const my_CkRegistroDelibLiquidatoriControl = getControl(amministrazioneControlloGroup, 'my_CkRegistroDelibLiquidatori');
            const my_AllElemLiquidatori: string = getControl(amministrazioneControlloGroup, 'my_AllLiquidatori.my_AllElemLiquidatori').value;
            const errors = my_CkRegistroDelibLiquidatoriControl.errors ? my_CkRegistroDelibLiquidatoriControl.errors : {};

            addRemoveError(isNilOrEmpty(my_CkRegistroDelibLiquidatoriControl?.value) && !isNilOrEmpty(my_AllElemLiquidatori), errors, 'my_CkRegistroDelibLiquidatori', {
                status: REQUIRED_STATUS,
            });

            my_CkRegistroDelibLiquidatoriControl.setErrors(isEqual(errors, {}) ? null : errors);
        });
    },

    my_DataAssembleaLiquidazVolontaria: (form: FormGroup) => {
        ruleGuard(form, ckNormeTargetValue, [], () => {
            const amministrazioneControlloGroup = getGroup(form, formPaths.ammControllo);
            const my_DataAssembleaLiquidazVolontariaControl = getControl(amministrazioneControlloGroup, 'my_DataAssembleaLiquidazVolontaria');
            const my_AllElemLiquidatori: string = getControl(amministrazioneControlloGroup, 'my_AllLiquidatori.my_AllElemLiquidatori').value;
            const errors = my_DataAssembleaLiquidazVolontariaControl.errors ? my_DataAssembleaLiquidazVolontariaControl.errors : {};
            const data = new Date(my_DataAssembleaLiquidazVolontariaControl.value);
            const resultAfter = isAfter(data, new Date('2079-06-06'));

            addRemoveError(isNilOrEmpty(my_DataAssembleaLiquidazVolontariaControl?.value) && !isNilOrEmpty(my_AllElemLiquidatori), errors, 'my_DataAssembleaLiquidazVolontaria', {
                status: REQUIRED_STATUS,
            });

            addRemoveError(resultAfter, errors, 'my_DataAssembleaLiquidazVolontaria', {
                status: DATE_NOT_ALLOWED,
            });

            my_DataAssembleaLiquidazVolontariaControl.setErrors(isEqual(errors, {}) ? null : errors);
        });
    },

    my_NoteAttivitaLiquidatori: (form: FormGroup) => {
        ruleGuard(form, ckNormeTargetValue, [], () => {
            const amministrazioneControlloGroup = getGroup(form, formPaths.ammControllo);
            const my_NoteAttivitaLiquidatoriControl = getControl(amministrazioneControlloGroup, 'my_NoteAttivitaLiquidatori');
            const my_AllElemLiquidatori: string = getControl(amministrazioneControlloGroup, 'my_AllLiquidatori.my_AllElemLiquidatori').value;
            const errors = my_NoteAttivitaLiquidatoriControl.errors ? my_NoteAttivitaLiquidatoriControl.errors : {};

            addRemoveError(isNilOrEmpty(my_NoteAttivitaLiquidatoriControl?.value) && !isNilOrEmpty(my_AllElemLiquidatori), errors, 'my_NoteAttivitaLiquidatori', {
                status: REQUIRED_STATUS,
            });

            my_NoteAttivitaLiquidatoriControl.setErrors(isEqual(errors, {}) ? null : errors);
        });
    },

    my_AllLiquidatoriCodFisc: (form: FormGroup) => {
        ruleGuard(form, ckNormeTargetValue, [], () => {
            const my_AllLiquidatoriElemFormArray = getFormArray(form, LIQUIDATORI_ARRAY_PATH);
            my_AllLiquidatoriElemFormArray.controls.map((group: FormGroup) => {
                codiceFiscaleHelper(group, 'my_AllLiquidatoriNome', 'my_AllLiquidatoriCognome', 'my_AllLiquidatoriCodFisc');
            });
        });
    },
});
